import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useResetPassword } from '@/hooks/auth.hooks';
import { AuthLayout } from '@/layouts/AuthLayout';
import { ResetPasswordManagement } from '../components/ResetPasswordManagement';
import { Loader } from '@/components/Loading/Loader';
import forgotPasswordCarIllusationUrl from '@/assets/svg/forgot-password-car.svg';
import { hideModal, showModal } from '@/utils/modal.utils';
import Modal from '@/components/Modal';
import {
  resetPasswordSchema,
  TResetPassword,
} from '@/schemas/auth/reset-password.schema';
import { ErrorMessage } from '@/components/Message/ErrorMessage';

export const ResetPassword = () => {
  const { t } = useTranslation('auth');
  const { token } = useParams();
  const navigate = useNavigate();
  const [responseError, setResponseError] = useState('');

  const {
    control,
    register,
    setValue,
    watch,
    setError,
    handleSubmit,
    formState: { errors },
  } = useForm<TResetPassword>({
    resolver: zodResolver(resetPasswordSchema),
  });

  const { mutate, isPending } = useResetPassword({
    handleSuccess: () => {
      showModal('#confirmation-reset-password-modal');
    },
    handleError: (error) => {
      if (error?.status === 404 || error?.error === 'Not Found') {
        setResponseError(t('couldNotFindAccount'));
      } else {
        setResponseError(t('common:errorOccurredTryAgainLater'));
      }
    },
  });

  const onSubmit = (data: TResetPassword) => {
    if (!responseError && token) {
      mutate({ token, password: data?.password });
    }
  };

  return (
    <>
      <AuthLayout
        illustrationUrl={forgotPasswordCarIllusationUrl}
        title={t('passwordReset')}
        description={t('enterYourNewPassword')}
      >
        {!token && (
          <div className="mb-5 flex justify-center">
            <ErrorMessage message={t('common:linkExpired')} />
          </div>
        )}
        <ResetPasswordManagement<TResetPassword>
          t={t}
          responseError={responseError}
          setResponseError={setResponseError}
          control={control}
          register={register}
          setValue={setValue}
          watch={watch}
          setError={setError}
          errors={errors}
          submitDisabled={isPending}
          onSubmit={handleSubmit(onSubmit)}
        />
      </AuthLayout>
      <Modal
        id="confirmation-reset-password-modal"
        title={t('common:confirmation')}
        centerY
        size="small"
        showCancelBtn={false}
        showSecondBtn
        secondBtnLabel={t('common:ok')}
        onClickSecondBtn={() => {
          hideModal('#confirmation-reset-password-modal');
          navigate('/login');
        }}
      >
        <div className="flex gap-2">
          <i className="-mt-1.5 ki-filled ki-check-circle text-2xl text-success" />
          <div>{t('passwordResetSuccessfully')}</div>
        </div>
      </Modal>
      {isPending && <Loader />}
    </>
  );
};
